import { i18n } from "@tellescope/i18n";
import { ensureHttps } from "@tellescope/utils";
import { useCallback, useMemo, useRef, useState } from "react";

import { GROUP_LABELS } from "../tokens/constants";
import { getTokenDefinition, TOKEN_DEFINITIONS } from "../tokens/registry";
import type { TokenDefinition, TokenGroupId } from "../tokens/types";

/** Props shared by every parameterized-token collector. */
export interface TokenCollectorProps {
  definition: TokenDefinition;
  onSubmit: (params: Record<string, string>) => void;
  onCancel: () => void;
  /** Pre-fills the collector for the edit-in-place flow; undefined on the insert path. */
  initialValues?: Record<string, string>;
}

/**
 * A palette-only entry (deliberately NOT in the registry, so it never affects recognition) that
 * lets the user pick any custom patient field. The resulting `{{enduser.<field>}}` is recognized
 * on reload via the value-namespace fallback and shows the field name.
 */
export const ENDUSER_CUSTOM_FIELD_DEFINITION: TokenDefinition = {
  id: "enduser.field",
  namespace: "enduser",
  groupId: "patient",
  kind: "value",
  getLabel: () => i18n.t("Other Patient Field…", { ns: "templates" }),
  params: [
    { key: "field", kind: "field", required: true, getLabel: () => i18n.t("Patient field", { ns: "templates" }) },
  ],
  build: (params) => `{{enduser.${params?.field ?? ""}}}`,
};

/**
 * Prepend `https://` to every `url`-kind param entered without a scheme (e.g. `testing.com/1234`),
 * so a scheme-less link resolves as a web link. Values that already carry a scheme (`http(s)://`,
 * `mailto:`, `tel:`, …) or are empty pass through unchanged — see {@link ensureHttps}.
 */
const normalizeUrlParams = (
  definition: TokenDefinition,
  params: Record<string, string>
): Record<string, string> => {
  const urlKeys = (definition.params ?? []).filter((param) => param.kind === "url").map((param) => param.key);
  return urlKeys.reduce(
    (acc, key) => (acc[key] === undefined ? acc : { ...acc, [key]: ensureHttps(acc[key]) }),
    params
  );
};

/** Display order of palette groups. */
const GROUP_ORDER: TokenGroupId[] = [
  "patient",
  "relatedContact",
  "sender",
  "organization",
  "brand",
  "calendarEvent",
  "links",
  "snippet",
  "placeholder",
  "signature",
  "other",
];

export interface TokenPaletteGroup {
  id: TokenGroupId;
  label: string;
  tokens: TokenDefinition[];
}

/** Viewport coordinates the collector popover anchors to (the clicked badge or the drop point). */
export interface CollectorAnchor {
  x: number;
  y: number;
}

/** Where to route the built token when the collector submits: the shared inserter, or a drop target. */
interface OpenCollector {
  definition: TokenDefinition;
  anchor: CollectorAnchor | null;
  insert: (raw: string) => void;
}

/** Arguments for opening the collector from a drop, once the dragged token id is resolved. */
export interface BeginDropCollectionArgs {
  tokenId: string;
  anchor: CollectorAnchor;
  /** Insert the built token at the captured drop position. */
  insertAt: (raw: string) => void;
}

/**
 * Palette state: groups the registry by namespace, filters by a label search, and tracks which
 * parameterized token (if any) has an open collector. Selecting a param-less token inserts it
 * immediately; selecting a parameterized one opens its collector, which builds the token on submit.
 */
export const useTokenPalette = (onInsert: (raw: string) => void) => {
  const [search, setSearch] = useState("");
  const [openCollector, setOpenCollector] = useState<OpenCollector | null>(null);
  const [openGroups, setOpenGroups] = useState<Set<TokenGroupId>>(new Set());
  // Latest open collector, read by the submit side effect so it never runs inside a state updater
  // (React re-invokes updaters, which would insert the token twice).
  const openCollectorRef = useRef(openCollector);
  openCollectorRef.current = openCollector;

  // Sections are collapsed by default but force-open while a search is narrowing results.
  const searchActive = search.trim().length > 0;
  const isGroupOpen = (id: TokenGroupId) => searchActive || openGroups.has(id);
  const setGroupOpen = (id: TokenGroupId, open: boolean) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });

  const groups = useMemo<TokenPaletteGroup[]>(() => {
    const query = search.trim().toLowerCase();
    // Match the friendly label, the token id (namespace.field), or the raw token string.
    const matches = (def: TokenDefinition) => {
      if (!query) return true;
      const searchValues = [def.getLabel(), def.id, ...(def.params ? [] : [def.build()])];
      return searchValues.some((value) => value.toLowerCase().includes(query));
    };
    const extrasByGroup: Partial<Record<TokenGroupId, TokenDefinition[]>> = {
      patient: [ENDUSER_CUSTOM_FIELD_DEFINITION],
    };
    return GROUP_ORDER.flatMap((id) => {
      const candidates = [...TOKEN_DEFINITIONS, ...(extrasByGroup[id] ?? [])];
      const tokens = candidates.filter((def) => def.groupId === id && matches(def));
      return tokens.length > 0 ? [{ id, label: GROUP_LABELS[id](), tokens }] : [];
    });
  }, [search]);

  // Selecting a parameterized token opens the collector anchored at the clicked badge, routing its
  // eventual submit to the shared inserter; a param-less token inserts immediately.
  const selectToken = useCallback(
    (definition: TokenDefinition, anchor: CollectorAnchor | null = null) => {
      if (definition.params?.length) {
        setOpenCollector({ definition, anchor, insert: onInsert });
        return;
      }
      onInsert(definition.build());
    },
    [onInsert]
  );

  // Drop entry point: resolve the dragged id (registry, plus the palette-only custom-field def) to a
  // definition and open its collector anchored at the drop point, routing submit to the drop target.
  const beginDropCollection = useCallback(({ tokenId, anchor, insertAt }: BeginDropCollectionArgs) => {
    const definition =
      tokenId === ENDUSER_CUSTOM_FIELD_DEFINITION.id ? ENDUSER_CUSTOM_FIELD_DEFINITION : getTokenDefinition(tokenId);
    if (!definition) return;
    setOpenCollector({ definition, anchor, insert: insertAt });
  }, []);

  const submitCollector = useCallback((params: Record<string, string>) => {
    const active = openCollectorRef.current;
    if (!active) return;
    active.insert(active.definition.build(normalizeUrlParams(active.definition, params)));
    setOpenCollector(null);
  }, []);

  const closeCollector = useCallback(() => setOpenCollector(null), []);

  return {
    search,
    setSearch,
    groups,
    isGroupOpen,
    setGroupOpen,
    activeCollector: openCollector?.definition ?? null,
    collectorAnchor: openCollector?.anchor ?? null,
    selectToken,
    beginDropCollection,
    submitCollector,
    closeCollector,
  };
};
